import { CalendarPlus, Check, Wrench } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { errText, frDate, frDateTime } from "@/components/pipeline/utils";
import { DateField } from "@/components/DateField";

const STATE = { late: ["status--overdue", "En retard"], today: ["status--due", "Aujourd'hui"], upcoming: ["status--active", "À venir"] };

const PlanForm = ({ onSave, busy, testid }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  return (
    <form className="cl-plan" onSubmit={(e) => { e.preventDefault(); onSave(date, time).then((ok) => ok && (setDate(""), setTime(""))); }} data-testid={testid}>
      <DateField className="pl-select" value={date} min={new Date().toISOString().slice(0, 10)} onChange={setDate} clearable={false} placeholder="Choisir la date" testid={`${testid}-date`} />
      <input type="time" className="pl-select" value={time} onChange={(e) => setTime(e.target.value)} data-testid={`${testid}-time`} />
      <button type="submit" className="btn btn--sm btn--ink" disabled={busy || !date} data-testid={`${testid}-save`}>Planifier</button>
    </form>
  );
};

const Task = ({ t, act, busy }) => {
  const [plan, setPlan] = useState(false);
  const [cls, label] = STATE[t.state];
  return (
    <li className={`cl-task cl-task--${t.state}`} data-testid={`client-task-${t.lead_id}`}>
      <p className="cl-task__row"><b>Relance {frDate(t.remind_date)}{t.remind_time ? ` · ${t.remind_time}` : ""}</b><span className={`status ${cls}`} data-testid={`client-task-state-${t.lead_id}`}>{label}</span></p>
      <Link to={`/pipeline?lead=${t.lead_id}`}>{t.title}</Link>
      <span className="cl-task__acts">
        <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => act({ lead_id: t.lead_id, action: "done" })} data-testid={`client-task-done-${t.lead_id}`}><Check size={13} /> Faite</button>
        {t.can_plan && <button type="button" className="btn btn--sm btn--ghost" onClick={() => setPlan(!plan)} data-testid={`client-task-plan-${t.lead_id}`}><CalendarPlus size={13} /> Prochaine relance</button>}
      </span>
      {plan && <PlanForm busy={busy} testid={`client-task-form-${t.lead_id}`} onSave={(d, h) => act({ lead_id: t.lead_id, action: "plan", remind_date: d, remind_time: h || null })} />}
    </li>
  );
};

const NewTask = ({ dossiers, act, busy }) => {
  const open = dossiers.filter((d) => ["todo", "standby"].includes(d.status));
  const [lead, setLead] = useState("");
  if (!open.length) return null;
  const id = Number(lead || open[0].lead_id);
  return (
    <div className="cl-new-task" data-testid="client-task-new">
      {open.length > 1 && <select className="pl-select" value={id} onChange={(e) => setLead(e.target.value)} data-testid="client-task-new-lead">{open.map((d) => <option key={d.lead_id} value={d.lead_id}>{d.title}</option>)}</select>}
      <PlanForm busy={busy} testid="client-task-new-form" onSave={(d, h) => act({ lead_id: id, action: "plan", remind_date: d, remind_time: h || null })} />
    </div>
  );
};

export const ClientTasks = ({ c, setC }) => {
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);
  const shown = all ? c.tasks : c.tasks.slice(0, 4);
  const act = (body) => {
    setBusy(true);
    const sync = body.lead_id > 0 ? " (copiée dans noCRM)" : " (fiche TEST, rien n'est envoyé à noCRM)";
    return api.post(`/clients/${c.id}/tasks`, body).then(({ data }) => { setC(data); toast.success(`${body.action === "done" ? "Relance marquée comme faite" : "Relance planifiée"}${sync}`); return true; }).catch((e) => { toast.error(errText(e)); return false; }).finally(() => setBusy(false));
  };
  return (
    <>
      {c.tasks.length ? <ul className="cl-tasks" data-testid="client-tasks-list">{shown.map((t) => <Task key={t.lead_id} t={t} act={act} busy={busy} />)}</ul> : <p className="ldx-miss" data-testid="client-tasks-empty">Aucun rappel programmé.</p>}
      {c.tasks.length > 4 && <button type="button" className="link-button cl-more" onClick={() => setAll(!all)} data-testid="client-tasks-more">{all ? "Réduire" : `Voir les ${c.tasks.length - 4} autres relances`}</button>}
      <p className="cl-mini">Planifier une relance</p>
      <NewTask dossiers={c.dossiers_list} act={act} busy={busy} />
      {c.tasks_done.length > 0 && <ul className="cl-done" data-testid="client-tasks-done">{c.tasks_done.map((t) => <li key={t.done_at}><Check size={12} /> Relance {t.remind_date ? `du ${frDate(t.remind_date)} ` : ""}faite le {frDateTime(t.done_at)}{t.by ? ` par ${t.by}` : ""}</li>)}</ul>}
    </>
  );
};

export const ClientSav = ({ c, setC }) => {
  const [busy, setBusy] = useState(false);
  const set = (status) => {
    setBusy(true);
    api.post(`/clients/${c.id}/sav`, { status }).then(({ data }) => { setC(data); toast.success(status === "open" ? "SAV ouvert" : "SAV marqué comme résolu"); }).catch((e) => toast.error(errText(e))).finally(() => setBusy(false));
  };
  const s = c.sav;
  const open = s?.status === "open";
  return (
    <div className={`cl-savbox ${open ? "is-open" : s ? "is-done" : ""}`} data-testid="client-sav">
      <p className="cl-savbox__head"><Wrench size={15} /><b data-testid="client-sav-status">{open ? "SAV ouvert" : s ? "SAV résolu" : "Aucun SAV"}</b>{s?.at && <small>{s.manual ? `${open ? "ouvert" : "résolu"} le ${frDate(s.at)}${s.by ? ` par ${s.by}` : ""}` : `signalé le ${frDate(s.at)}`}</small>}</p>
      {s?.reason && s.reason !== "Saisie manuelle" && <p className="cl-savbox__why" data-testid="client-sav-reason">{s.reason}{s.excerpt ? ` : « ${s.excerpt} »` : ""}</p>}
      <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => set(open ? "resolved" : "open")} data-testid="client-sav-toggle">{open ? "Marquer résolu" : s ? "Rouvrir le SAV" : "Signaler un SAV"}</button>
    </div>
  );
};
